import React, { useState } from 'react';
import { FileText, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import TermsAndConditions from './TermsAndConditions';
import PrivacyPolicy from './PrivacyPolicy';

const LegalAcceptance = ({ onAccept, userEmail }) => {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [error, setError] = useState('');

  const handleAccept = () => {
    if (!acceptedTerms || !acceptedPrivacy) {
      setError('Please accept both the Terms & Conditions and Privacy Policy to continue.');
      return;
    }

    // Store acceptance in localStorage with timestamp
    const acceptanceData = {
      termsAccepted: true,
      privacyAccepted: true,
      timestamp: new Date().toISOString(),
      email: userEmail
    };

    try {
      localStorage.setItem('legal_acceptance', JSON.stringify(acceptanceData));
      onAccept(acceptanceData);
    } catch (error) {
      console.error('Error saving legal acceptance:', error);
      onAccept(acceptanceData); // Still proceed even if localStorage fails
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center">
                <Shield className="w-8 h-8 text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-2">
              Welcome to PlanLens
            </h2>
            <p className="text-center text-gray-600">
              Before you begin, please review and accept our legal documents
            </p>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto flex-1">
            {/* Important Notice */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-6">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-900 mb-1">Important</p>
                  <p className="text-sm text-blue-800">
                    PlanLens provides educational financial information only. We do not provide financial
                    advice. Always consult with qualified financial advisors before making investment decisions.
                  </p>
                </div>
              </div>
            </div>

            {/* Terms & Conditions Card */}
            <div className="mb-6 border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Terms and Conditions</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Outlines your rights and responsibilities when using PlanLens, including our educational
                    purpose, limitation of liability, and user obligations.
                  </p>
                  <button
                    onClick={() => setShowTerms(true)}
                    className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center space-x-1 hover:underline"
                  >
                    <span>Read Full Terms & Conditions</span>
                    <span>→</span>
                  </button>
                </div>
              </div>

              {/* Terms Checkbox */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="flex items-start space-x-3 cursor-pointer group">
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => {
                        setAcceptedTerms(e.target.checked);
                        setError('');
                      }}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                    />
                    {acceptedTerms && (
                      <CheckCircle className="absolute inset-0 w-5 h-5 text-green-600 pointer-events-none" />
                    )}
                  </div>
                  <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                    I have read and agree to the <strong>Terms and Conditions</strong>
                  </span>
                </label>
              </div>
            </div>

            {/* Privacy Policy Card */}
            <div className="mb-6 border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Privacy Policy</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Explains how we collect, use, and protect your personal information. We do not sell your data
                    and comply with UK GDPR and US CCPA regulations.
                  </p>
                  <button
                    onClick={() => setShowPrivacy(true)}
                    className="text-green-600 hover:text-green-700 font-semibold text-sm flex items-center space-x-1 hover:underline"
                  >
                    <span>Read Full Privacy Policy</span>
                    <span>→</span>
                  </button>
                </div>
              </div>

              {/* Privacy Checkbox */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="flex items-start space-x-3 cursor-pointer group">
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      checked={acceptedPrivacy}
                      onChange={(e) => {
                        setAcceptedPrivacy(e.target.checked);
                        setError('');
                      }}
                      className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 focus:ring-2 cursor-pointer"
                    />
                    {acceptedPrivacy && (
                      <CheckCircle className="absolute inset-0 w-5 h-5 text-green-600 pointer-events-none" />
                    )}
                  </div>
                  <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                    I have read and agree to the <strong>Privacy Policy</strong>
                  </span>
                </label>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}

            {/* Key Points */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs font-semibold text-gray-900 mb-2">Key Points:</p>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>✓ Educational tool - not financial advice</li>
                <li>✓ Your data is encrypted and secure</li>
                <li>✓ We do not sell your personal information</li>
                <li>✓ Compliant with UK GDPR and US CCPA</li>
                <li>✓ You can request data deletion anytime</li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleAccept}
              disabled={!acceptedTerms || !acceptedPrivacy}
              className={`w-full py-4 rounded-xl font-semibold text-white transition-all ${
                acceptedTerms && acceptedPrivacy
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {acceptedTerms && acceptedPrivacy ? (
                <span className="flex items-center justify-center space-x-2">
                  <CheckCircle className="w-5 h-5" />
                  <span>Accept & Continue</span>
                </span>
              ) : (
                'Accept Both to Continue'
              )}
            </button>
            <p className="text-xs text-gray-500 text-center mt-3">
              By clicking "Accept & Continue", you agree to our Terms and Privacy Policy
            </p>
          </div>
        </div>
      </div>

      {/* Terms Modal */}
      {showTerms && (
        <TermsAndConditions onClose={() => setShowTerms(false)} />
      )}

      {/* Privacy Modal */}
      {showPrivacy && (
        <PrivacyPolicy onClose={() => setShowPrivacy(false)} />
      )}
    </>
  );
};

export default LegalAcceptance;
